import { Button, Field, Input, Select } from "@kontaktfix/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LogoMark } from "@/components/logo";
import { api } from "@/lib/api";
import { storedAttribution } from "@/lib/attribution";
import { toastError } from "@/lib/errors";
import { useMe } from "@/lib/queries";

export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [language, setLanguage] = useState(i18n.language === "en" ? "en" : "de");
  const [busy, setBusy] = useState(false);

  const logout = async () => {
    await api.post("/api/auth/logout");
    client.clear();
    await navigate({ to: "/login" });
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/orgs", { name, language, attribution: storedAttribution() });
      await client.invalidateQueries();
      await navigate({ to: "/" });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-subtle px-4">
      <div className="w-full max-w-[420px]">
        <div className="mb-6 flex justify-center">
          <LogoMark size={36} />
        </div>
        <div className="rounded-lg border border-border bg-bg p-6 shadow-md">
          {me?.canCreateOrg ? (
            <form onSubmit={create} className="flex flex-col gap-4">
              <div>
                <h1 className="text-lg font-semibold tracking-tight">{t("onboarding.createTitle")}</h1>
                <p className="mt-1 text-[13px] text-fg-muted">{t("onboarding.createText")}</p>
              </div>
              <Field label={t("onboarding.orgName")} htmlFor="org-name">
                <Input
                  id="org-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
              </Field>
              <Field label={t("onboarding.language")} htmlFor="org-lang">
                <Select
                  id="org-lang"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as "de" | "en")}
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </Select>
              </Field>
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!name.trim()}
                className="justify-center"
              >
                {t("onboarding.create")}
              </Button>
            </form>
          ) : (
            <div>
              <h1 className="text-lg font-semibold tracking-tight">{t("onboarding.noAccessTitle")}</h1>
              <p className="mt-1 text-[13px] text-fg-muted">{t("onboarding.noAccessText")}</p>
              <p className="mt-4 text-[13px] text-fg-muted">{t("onboarding.claimText")}</p>
              <Button asChild className="mt-3">
                {/* Microsoft only lets tenant administrators through (routes/auth.ts claimAdmin). */}
                <a href="/api/auth/login?claim=1">{t("onboarding.claim")}</a>
              </Button>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center justify-between text-[12.5px] text-fg-muted">
          <span>{t("onboarding.signedInAs", { email: me?.identity.email })}</span>
          <button type="button" className="hover:text-fg" onClick={logout}>
            {t("nav.logout")}
          </button>
        </div>
      </div>
    </div>
  );
}
